// El hilo de comentarios del producto abierto —
// ADR-2026-09-08-comentarios-en-el-producto.
//
// Se carga cuando se abre el panel y no con el lanzamiento: son textos que casi
// nunca se miran, y traerlos de todos los productos en cada carga sería pagar
// siempre por algo que se usa a veces. Lo que SÍ viaja con la pantalla es el
// conteo de pendientes, que es un número por producto.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  borrarComentario,
  comentar,
  fetchComentarios,
  resolverComentario,
  type ComentarioDeProducto,
} from "./lanzamientosApi";

export function useComentarios(lanzamientoProductoId: string | null, companyId: string | null) {
  const [comentarios, setComentarios] = useState<ComentarioDeProducto[]>([]);
  const [cargando, setCargando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Quién soy: decide qué comentarios se pueden borrar. Se pide una sola vez.
  useEffect(() => {
    void (async () => {
      const { data } = await supabase.auth.getUser();
      setUserId(data.user?.id ?? null);
    })();
  }, []);

  const cargar = useCallback(async () => {
    if (!lanzamientoProductoId) {
      setComentarios([]);
      return;
    }
    setCargando(true);
    setComentarios(await fetchComentarios(lanzamientoProductoId, userId));
    setCargando(false);
  }, [lanzamientoProductoId, userId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const conTrabajo = async (accion: () => Promise<{ error: string | null }>) => {
    setTrabajando(true);
    setError(null);
    const r = await accion();
    if (r.error) setError(r.error);
    else await cargar();
    setTrabajando(false);
    return r.error;
  };

  const dejar = useCallback(
    async (texto: string) => {
      if (!lanzamientoProductoId || !companyId || !userId) {
        setError("No se pudo dejar el comentario");
        return;
      }
      await conTrabajo(() => comentar(lanzamientoProductoId, companyId, userId, texto));
    },
    // `conTrabajo` se recrea en cada render y meterlo acá recrearía `dejar`
    // también; lo que importa es que cambie con el producto y la sesión.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lanzamientoProductoId, companyId, userId, cargar],
  );

  const resolver = useCallback(
    async (id: string, resuelto: boolean, esVisitante?: boolean) => {
      await conTrabajo(() => resolverComentario(id, resuelto, esVisitante));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cargar],
  );

  const borrar = useCallback(
    async (id: string, esVisitante?: boolean) => {
      await conTrabajo(() => borrarComentario(id, esVisitante));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cargar],
  );

  return { comentarios, cargando, trabajando, error, dejar, resolver, borrar };
}
